<script>
    import { params, redirect } from "@roxi/routify";
    import Card from "$/components/card/Card.svelte";
    import LoadingMessage from "../_templates/LoadingMessage.svelte";
    import EmptyStateMessage from "../_templates/EmptyStateMessage.svelte";
    import { TokenStore } from "$/stores/TokenStore";
    import { TenantStore } from "$/stores/TenantStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { TopicService } from "$/services/TopicService";
    import { formattedDate } from "$/utils/date";
    import ErrorImg from "$/images/error/Error-500.png";
    import Error401Img from "$/images/error/Error-401.png";
    import Error403Img from "$/images/error/Error-403.png";
    import Error404Img from "$/images/error/Error-404.png";
    import Sortable from "sortablejs";
    import { onDestroy, onMount } from "svelte";
    import {
        getFullName,
        getRole,
        getRootDocument,
        isPdfViewerUrl,
        PAGE_WRAPPER_ID,
    } from "$/utils/Utils";
    import { StoreStatus } from "$/models/StoreStatus";
    import { ClusterStore } from "$/stores/ClusterStore";

    let user = {
        email: "",
        firstName: "Anonymous",
        id: "0",
        lastName: "",
        questionnaireDone: true,
        username: "anonymous",
    };

    let userTopicRole = TopicAccessLevel.NO_ACCESS;
    let errorStatus = null;
    let sortableDragAndDrop;

    $: createdBy = getFullName(
        ActiveTopicStore.getOwnerRole($ActiveTopic)?.user,
    );

    async function getSharedTopic() {
        let topic = {};

        // try to get topic if is public
        try {
            topic = await TopicService.getSharedTopic($params.topicId);
            ActiveTopicStore.set({
                topic,
                state: {
                    status: StoreStatus.UP_TO_DATE,
                    id: topic.id,
                },
            });
        } catch (e) {
            // if topic is not public error should be 401
            errorStatus = e.error.status;
            if (errorStatus === 404) return;
        }

        if (!$TokenStore.initialized) {
            try {
                await TokenStore.init().then(() => {
                    if ($TokenStore.user) {
                        user = $TokenStore.user;
                    }
                });
            } catch (_) {}
        } else if ($TokenStore.user) {
            user = $TokenStore.user;
        }

        if ($TenantStore === null && !$TokenStore.expired) {
            try {
                await TenantStore.init();
            } catch (_) {}
        }

        // if we have user and tenant
        // try to get topic with token
        // and if we get topic show it else show error
        if ($TokenStore.user && $TenantStore) {
            try {
                await ActiveTopicStore.init(
                    $params.topicId,
                    TokenStore,
                    TenantStore,
                    ClusterStore,
                    false,
                );
                errorStatus = null;
            } catch (_) {}
        }

        if ($ActiveTopicStore.state.error) {
            errorStatus = $ActiveTopicStore.state.error;
        } else {
            userTopicRole =
                $ActiveTopic && $TokenStore.user && $TenantStore
                    ? getRole(
                          $ActiveTopic,
                          $TokenStore.user.id,
                          $TenantStore?.tenants,
                      )
                    : TopicAccessLevel.NO_ACCESS;

            if (userTopicRole >= TopicAccessLevel.COMMENTER) {
                await ActiveTopicStore.fetchCollaborators();
            }

            return topic;
        }
    }

    async function addTopicToVisited() {
        if (!$TokenStore.user) return;

        let visitorRole = {
            role: TopicAccessLevel.NO_ACCESS,
            user: {
                id: $TokenStore.user.id,
            },
        };

        await TopicService.createTopicRole($params.topicId, visitorRole);
    }

    function handleTopicNameEdited(event) {
        let topicNameEdited = event.target.value;
        if (topicNameEdited.trim() === "") {
            topicNameEdited = "Untitled Topic";
            event.target.value = topicNameEdited;
        }
        ActiveTopicStore.updateTopic({
            ...$ActiveTopic,
            name: topicNameEdited,
        });
    }

    // start of scroll left/top and right/bottom on dragenter and dragleave
    let scrollInterval;
    let snippetsWrapperForHorizontalScroll;

    let leftTopScrollHover = false;

    function scrollLeftTop() {
        sortableDragAndDrop.option("disabled", true);
        leftTopScrollHover = true;

        // if we are in mobile scroll top
        if (window.innerWidth < 768)
            scrollInterval = setInterval(() => {
                document.querySelector("html").scrollTop -= 10;
                if (document.querySelector("html").scrollTop === 0)
                    stopScroll();
            }, 10);
        // else scroll to left
        else
            scrollInterval = setInterval(() => {
                snippetsWrapperForHorizontalScroll.scrollLeft -= 10;
                if (snippetsWrapperForHorizontalScroll.scrollLeft === 0)
                    stopScroll();
            }, 10);
    }

    let rightBottomScrollHover = false;

    function scrollRightBottom() {
        sortableDragAndDrop.option("disabled", true);
        rightBottomScrollHover = true;

        // if we are mobile scroll bottom
        if (window.innerWidth < 768)
            scrollInterval = setInterval(() => {
                document.querySelector("html").scrollTop += 10;
                if (
                    document.querySelector("html").scrollTop ===
                    document.querySelector("html").scrollHeight -
                        document.querySelector("html").clientHeight
                )
                    stopScroll();
            }, 10);
        // else scroll to right
        else
            scrollInterval = setInterval(() => {
                snippetsWrapperForHorizontalScroll.scrollLeft += 10;
                if (
                    snippetsWrapperForHorizontalScroll.scrollLeft ===
                    snippetsWrapperForHorizontalScroll.scrollWidth -
                        snippetsWrapperForHorizontalScroll.clientWidth
                )
                    stopScroll();
            }, 10);
    }

    function stopScroll() {
        leftTopScrollHover = false;
        rightBottomScrollHover = false;
        sortableDragAndDrop.option("disabled", userTopicRole < TopicAccessLevel.EDITOR);
        clearInterval(scrollInterval);
        scrollInterval = null;
    }

    // end of scroll left/top and right/bottom on dragenter and dragleave

    const dragAndDropConfig = {
        handle: ".card-header",
        animation: 150,
        delay: 200,
        delayOnTouchOnly: true,
        onStart(event) {
            // Add quick vibration feedback when drag starts
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
        },
        onEnd(event) {
            // When the user is dragging a snippet at the very end of the right/left
            // side of the page and drops the snippet the dragleave event won't fire so if
            // the scroll is still active after we drop the snippet,
            // stop the scroll and return the element to it's original position
            if (scrollInterval) {
                const items = event.from.querySelectorAll(":scope > div");
                event.from.insertBefore(event.item, items[event.oldIndex]);
                stopScroll();
                return false;
            }

            ActiveTopicStore.changeSnippetPosition(
                event.oldIndex,
                event.newIndex,
            );
        },
    };

    const setDragAndDropPermission = () => {
        sortableDragAndDrop.option(
            "disabled",
            userTopicRole < TopicAccessLevel.EDITOR,
        );
    };

    const setDragAndDropCopyEffect = (event) => {
        event.dataTransfer.effectAllowed = "copyMove";
    };

    /**
     * Algorithm:
     * 1. If logged in try to get topic with token
     *    1.TRUE - Show topic
     *    1.FALSE - If error show error
     *
     * 2. If not logged in try to get topic without token
     *    2.TRUE - Show topic in view only mode
     *    2.FALSE - If 401 error redirect to login and go to 1.
     */

    $: {
        // If user is annonymous and topic is loaded append view only mode to PDF urls
        if ($ActiveTopic && !$TokenStore.user) {
            $ActiveTopic.snippets.forEach((snippet) => {

                if (!isPdfViewerUrl(snippet.url)) return;

                let viewOnlyParams =
                    "&viewOnly=true&pageNumber=" + snippet.highlight.pageNumber;

                // if there's #:~:text= in url add viewOnlyParams before it else add it at the end
                if (snippet.url.includes("#:~:text")) {
                    snippet.url = snippet.url.replace(
                        "#:~:text",
                        viewOnlyParams + "#:~:text",
                    );
                } else {
                    snippet.url += viewOnlyParams;
                }
            });
        }
    }

    $: if (snippetsWrapperForHorizontalScroll && !sortableDragAndDrop) {
        sortableDragAndDrop = new Sortable(
            snippetsWrapperForHorizontalScroll,
            dragAndDropConfig,
        );
    }

    onMount(() => {
        getRootDocument().addEventListener(
            "dragstart",
            setDragAndDropCopyEffect,
        );
    });

    onDestroy(() => {
        getRootDocument().removeEventListener(
            "dragstart",
            setDragAndDropCopyEffect,
        );
    });

    // on user topic role change set permission to drag and drop snippets
    $: userTopicRole, sortableDragAndDrop && setDragAndDropPermission();
</script>

{#await getSharedTopic()}
    <LoadingMessage>Loading topic...</LoadingMessage>
{:then _}
    <!-- if we get 401 that topic is not public and we do not have user logged in-->
    {#if errorStatus === 401 && !$TokenStore.user}
        <div class="loading">
            <img class="error-img" src={Error401Img} alt="Error 401" />
            <div class="loading__text">
                <span>You need to login to see this topic</span>
            </div>
            <button
                on:click={() =>
                    $redirect(`/auth/login?redirectUrl=/v/${$params.topicId}`)}
                >Login
            </button>
        </div>
        <!-- if we get 403 that topic is not shared-->
    {:else if errorStatus === 403}
        <div class="loading">
            <img class="error-img" src={Error403Img} alt="Error 403" />
            <div class="loading__text">
                <span>You do not have access to this topic</span>
            </div>
            <button on:click={() => $redirect(`/`)}>Go to Dashboard</button>
        </div>
        <!-- if we get 404 that topic is not found-->
    {:else if errorStatus === 404}
        <div class="loading">
            <img class="error-img" src={Error404Img} alt="Error 404" />
            <div class="loading__text">
                <span>Topic not found</span>
            </div>
            <button on:click={() => $redirect(`/`)}>Go to Dashboard</button>
        </div>
        <!-- if we get anything else that is not 401, 403 or 404 -->
    {:else if errorStatus && errorStatus !== 401 && errorStatus !== 403 && errorStatus !== 404}
        <div class="loading">
            <img class="error-img" src={ErrorImg} alt="Error" />
            <div class="loading__text">
                <span>Please check URL, topic ID format might be wrong</span>
            </div>
            <button on:click={() => $redirect(`/`)}>Go to Dashboard</button>
        </div>
        <!-- if we get topic data -->
    {:else if !errorStatus}
        {#await addTopicToVisited()}
            <LoadingMessage>Checking permissions...</LoadingMessage>
        {:then _}
            <div id={PAGE_WRAPPER_ID} class="topic-page-wrapper">
                <div class="topic-header">
                    <!-- Topic Header -->
                    <div class="topic-header__info">
                        {#if userTopicRole < TopicAccessLevel.EDITOR}
                            <span class="topic-header__info-heading">
                                {$ActiveTopic.name}
                            </span>
                        {:else}
                            <input
                                class="topic-header__info-heading"
                                value={$ActiveTopic.name}
                                on:change={handleTopicNameEdited}
                            />
                        {/if}
                        <div class="topic-header__info-footer">
                            {#if $ActiveTopic && $ActiveTopic.archived}
                                <span class="topic-label">Archived</span>
                            {/if}

                            <span>{formattedDate($ActiveTopic.createdOn)}</span>

                            {#if createdBy}
                                <span>-</span>
                                <span>{createdBy}</span>
                            {/if}
                        </div>
                    </div>
                </div>

                <div
                    class="snippets_wrapper"
                    bind:this={snippetsWrapperForHorizontalScroll}
                >
                    {#if $ActiveTopic === null}
                        <LoadingMessage></LoadingMessage>
                    {:else if !$ActiveTopic?.snippets || $ActiveTopic?.snippets.length === 0}
                        <div class="snippets_wrapper--none">
                            <EmptyStateMessage type="snippet"
                            ></EmptyStateMessage>
                        </div>
                    {:else}
                        {#each $ActiveTopic.snippets as snippet, index (snippet.id)}
                            <div class="card_wrapper">
                                <Card
                                    card={snippet}
                                    editRole={userTopicRole}
                                    {user}
                                    topicId={$params.topicId}
                                    deleteSnippet={ActiveTopicStore.deleteSnippet}
                                    createComment={ActiveTopicStore.createComment}
                                    updateComment={ActiveTopicStore.updateComment}
                                    deleteComment={ActiveTopicStore.deleteComment}
                                    openInNewTab={true}
                                    ordinal={index}
                                />
                            </div>
                        {/each}
                        <div
                            class="cw-invisible-scroll-bar cw-invisible-left"
                            class:cw-invisible-scroll-bar--hover={leftTopScrollHover}
                            on:dragenter={scrollLeftTop}
                            on:dragleave={stopScroll}
                        ></div>
                        <div
                            class="cw-invisible-scroll-bar cw-invisible-right"
                            class:cw-invisible-scroll-bar--hover={rightBottomScrollHover}
                            on:dragenter={scrollRightBottom}
                            on:dragleave={stopScroll}
                        ></div>
                    {/if}
                </div>
            </div>
        {/await}
    {/if}
{/await}

<style>
    @import "../../main.css";

    .cw-invisible-scroll-bar {
        @apply fixed z-40 h-0 w-0 bg-transparent;
    }

    .cw-invisible-scroll-bar--hover {
        @apply h-20 w-full sm:h-full sm:w-20 !important;
    }

    .cw-invisible-left {
        @apply left-0 top-0;
    }

    .cw-invisible-right {
        @apply bottom-0 right-0;
    }

    .topic-page-wrapper {
        @apply mb-auto flex w-full flex-col overflow-hidden;
    }

    .topic-header {
        @apply z-40 flex items-center justify-between gap-1 bg-white p-5;
    }

    .topic-header__info {
        @apply flex flex-1 flex-col;
    }

    .topic-header__info-heading {
        @apply w-full bg-white text-xl font-semibold outline-none;
    }

    .topic-header__info-footer {
        @apply flex w-fit flex-row items-center gap-1 text-xs;
    }

    .snippets_wrapper {
        @apply flex flex-grow flex-col items-center gap-6 overflow-x-hidden px-4 md:flex-row md:flex-nowrap md:items-start md:overflow-x-auto md:overflow-y-clip;
    }

    .snippets_wrapper--none {
        @apply flex flex-grow flex-col items-center justify-center;
        min-height: calc(100vh - 188px);
    }

    .card_wrapper {
        @apply z-20 w-full md:relative md:h-full md:w-96 md:flex-[0_0_auto] md:overflow-y-auto;
        max-height: calc(100vh - 200px);
        min-height: calc(100vh - 200px);
    }

    @media (max-width: 768px) {
        .card_wrapper {
            min-height: auto;
            max-height: none !important;
        }
    }

    .card_wrapper::after {
        content: "";
        @apply sticky bottom-0 z-50 -mb-4 block h-4 w-full bg-gradient-to-t from-white to-transparent;
    }

    .card_wrapper::-webkit-scrollbar {
        @apply h-3 w-3;
    }

    .card_wrapper::-webkit-scrollbar-thumb {
        @apply bg-transparent;
    }

    .card_wrapper:hover::-webkit-scrollbar-thumb {
        @apply bg-gray-200 hover:bg-gray-300;
    }

    .delete-topic-modal h1 {
        @apply mb-2 text-center text-2xl font-bold;
    }

    .delete-topic-modal p {
        @apply px-2 text-center text-gray-600;
    }

    .loading {
        @apply reset top-0 z-30 my-auto flex flex-col items-center justify-center !important;
    }

    .loading .error-img {
        @apply reset w-64 sm:w-[30rem] !important;
    }

    .loading__text {
        @apply reset mt-2 text-base !important;
    }

    .loading button {
        @apply mt-8 rounded-xl bg-pink-500 p-2 font-semibold text-white;
    }

    .topic-label {
        @apply mr-0.5 mt-0.5 select-none rounded-full bg-gray-200 px-2 py-0.5 text-3xs font-bold uppercase leading-tight tracking-wide text-gray-500 hover:bg-gray-300 hover:text-gray-900 !important;
    }
</style>
